import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { NAVIGABLE_BASE_TYPES } from '../../Helpers/baseTypeUtils';
import { BreadcrumbComponent, BreadcrumbItem } from "../../Components/BreadCrumb";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { BaseComponentImpl, RoomImpl, LevelImpl } from "../../types/implementations";
import { useBuilding } from "../../Helpers/BuildingContext";
import { getComponentRoomKey } from "../../Helpers/ComponentRoomUtils";
import { Building, PhotoModel, PhotoLocation } from "../../types/protocols";
import { hasPhotoLocation as hasAnyPhotoLocation } from "../../Helpers/photoLocations";
import { BaseType, OutdoorCategory, UtilityCategory } from "../../types/Enums";
import ComponentDetailsView from "./ComponentDetailsView";
import { useFirebaseImage, resolveFirebaseImageUrl } from "../../Helpers/ImageLoader";
import { useUsersData } from "../../Helpers/UsersContext";
import { buildPhotoCaption } from "../../Helpers/photoCaptionUtils";
import { Fancybox } from "@fancyapps/ui";
import { useBuildComponentTypes } from "../../contexts/BuildComponentTypesContext";
import { translate } from "../../types/EnumTranslations";
import ReportIssueModal from "../../Components/ReportIssueModal";
import { DeleteComponentModal } from '../Förvaltning/ComponentValidation/DeleteComponentModal';
import ComponentActionsDropdown from "../../Components/ComponentActionsDropdown";
import { useToast } from "../../Components/ToastContext";
import "../Room/RoomComponentDetailsPane.css";
import { PageLoader } from "../../Components/LoadingSpinner";
import PhotoWithMarker from "../../Components/PhotoWithMarker";
import { orderPhotos } from "../../Helpers/photoOrdering";

// Component for image preview with proper Firebase loading
const ImagePreview: React.FC<{
    photo: PhotoModel;
    zoomLevel: number;
    panPosition: { x: number; y: number };
    onWheel: (e: React.WheelEvent) => void;
    onClick: (imageUrl: string) => void;
    onMouseDown: (e: React.MouseEvent) => void;
    alt: string;
    isDragging: boolean;
}> = ({ photo, zoomLevel, panPosition, onWheel, onClick, onMouseDown, alt, isDragging }) => {
    const { src, isLoading, hasError } = useFirebaseImage(photo.storagePath);

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: '#666'
            }}>
                Laddar bild...
            </div>
        );
    }

    if (hasError) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: '#999',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
            }}>
                Kunde inte ladda bild
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                transition: isDragging ? 'none' : 'transform 0.1s ease',
                objectFit: 'contain',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none'
            }}
            onWheel={onWheel}
            onClick={() => onClick(src)}
            onMouseDown={onMouseDown}
            draggable={false}
        />
    );
};

const ComponentPage: React.FC = () => {
    const [component, setComponent] = useState<BaseComponentImpl | null>(null);
    const { buildingKey, itemId } = useParams();
    const navigate = useNavigate();
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();
    const { getTranslation } = useBuildComponentTypes();
    const { showToast } = useToast();

    // Set document title based on state
    useDocumentTitle(
        loadingBuilding ? 'Laddar komponent...' :
        buildingError ? 'Fel - Komponent' :
        !currentBuilding ? 'Komponent' :
        !component ? 'Komponent' :
        `${translate(component.type)}: ${component.displayName(currentBuilding)}`
    );

    const { users } = useUsersData();

    // State for selected image and zoom
    const [selectedImage, setSelectedImage] = useState<PhotoModel | null>(null);
    // The photo set the selected image belongs to, so the preview can step
    // between siblings (arrow keys / on-screen arrows) and open the whole set
    // as a FancyBox gallery.
    const [selectedPhotos, setSelectedPhotos] = useState<PhotoModel[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<{ name: string; url: string } | null>(null);
    const [selectedPhotoLocation, setSelectedPhotoLocation] = useState<PhotoLocation | null>(null);
    const [showReportIssueModal, setShowReportIssueModal] = useState(false);
    const [showChildReportIssueModal, setShowChildReportIssueModal] = useState(false);
    // The component the delete dialog is open for: this page's component or the selected child
    const [deleteTarget, setDeleteTarget] = useState<BaseComponentImpl | null>(null);
    // The child shown in the preview pane, by key so it is read from the building
    // on every render and shows edits made in the pane
    const [selectedChildKey, setSelectedChildKey] = useState<string | null>(null);
    const selectedChildComponent = useMemo(() => {
        const data = selectedChildKey ? currentBuilding?.model.structure?.components?.[selectedChildKey] : undefined;
        return data ? new BaseComponentImpl(data) : null;
    }, [selectedChildKey, currentBuilding]);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [hasDragged, setHasDragged] = useState(false);
    const previewRef = useRef<HTMLDivElement>(null);

    function resolveRoomFromComponentOrParents(
        component: BaseComponentImpl,
        currentBuilding: Building | null,
    ): RoomImpl | undefined {
        if (!currentBuilding?.model?.structure) return undefined;
        const { rooms = {}, components = {} } = currentBuilding.model.structure;

        const roomFromKey = (rk?: string) => {
            if (!rk) return undefined;
            const rm = rooms[rk];
            return rm ? new RoomImpl(rm) : undefined;
        };

        const directRoom = roomFromKey(getComponentRoomKey(component) ?? undefined);
        if (directRoom) return directRoom;

        // Always use the first parent room key
        const parentRoomKeys = component.parentRoomKeys?.value;
        if (Array.isArray(parentRoomKeys) && parentRoomKeys.length > 0) {
            const r = roomFromKey(parentRoomKeys[0]);
            if (r) return r;
        }

        // Always use the first parent surface key
        const parentSurfaceKeys: Array<string | undefined> = [
            component.parentSurface?.value ?? undefined,
            component.parentSurface1?.value ?? undefined,
            component.parentSurface2?.value ?? undefined,
        ];

        // Get the first non-empty parent surface key
        const firstParentKey = parentSurfaceKeys.find(key => key !== undefined);
        if (firstParentKey) {
            const parentCompRaw = components[firstParentKey];
            if (parentCompRaw) {
                const parentComp = new BaseComponentImpl(parentCompRaw);

                const parentDirectRoom = roomFromKey(getComponentRoomKey(parentComp) ?? undefined);
                if (parentDirectRoom) return parentDirectRoom;

                const parentPRK = parentComp.parentRoomKeys?.value;
                if (Array.isArray(parentPRK) && parentPRK.length > 0) {
                    const r = roomFromKey(parentPRK[0]);
                    if (r) return r;
                }
            }
        }

        return undefined;
    }

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    useEffect(() => {
        if (currentBuilding && itemId) {
            const data = currentBuilding?.model.structure?.components?.[itemId] ?? null;
            if (data) {
                setComponent(new BaseComponentImpl(data))
            }
        }
    }, [currentBuilding, itemId])

    // Reset all preview state to defaults
    const resetPreviewState = useCallback(() => {
        setSelectedImage(null);
        setSelectedPhotos([]);
        setSelectedChildKey(null);
        setSelectedDocument(null);
        setSelectedPhotoLocation(null);
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
        setHasDragged(false);
    }, []);

    // Handle image selection. `allPhotos` is the sibling set the photo belongs to
    // (the component's "Foton"); when omitted (e.g. a lone child photo) the photo
    // is previewed on its own.
    const handleImageSelect = useCallback((photo: PhotoModel, allPhotos?: { [key: string]: PhotoModel }) => {
        resetPreviewState();
        setSelectedPhotos(allPhotos ? orderPhotos(allPhotos) : [photo]);
        setSelectedImage(photo);
    }, [resetPreviewState]);

    // Step the preview to the previous/next sibling photo (wraps around)
    const goToAdjacentPhoto = useCallback((direction: 1 | -1) => {
        const entries = selectedPhotos;
        if (entries.length < 2 || !selectedImage) return;
        const idx = entries.findIndex(p => p.key === selectedImage.key);
        if (idx < 0) return;
        const nextIdx = (idx + direction + entries.length) % entries.length;
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
        setHasDragged(false);
        setSelectedImage(entries[nextIdx]);
    }, [selectedPhotos, selectedImage]);

    // Handle photo location selection (Position i foto)
    const handlePhotoLocationSelect = useCallback((photoLocation: PhotoLocation) => {
        resetPreviewState();
        setSelectedPhotoLocation(photoLocation);
    }, [resetPreviewState]);

    // Handle child component selection
    const handleChildComponentSelect = useCallback((childComponent: BaseComponentImpl) => {
        resetPreviewState();
        setSelectedChildKey(childComponent.key);
    }, [resetPreviewState]);

    // Handle document selection
    const handleDocumentSelect = useCallback((doc: { name: string }, url: string) => {
        resetPreviewState();
        setSelectedDocument({ name: doc.name, url });
    }, [resetPreviewState]);

    // Arrow-key navigation between sibling photos while a photo is previewed
    useEffect(() => {
        if (!selectedImage) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                goToAdjacentPhoto(-1);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                goToAdjacentPhoto(1);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, goToAdjacentPhoto]);

    // Handle mouse wheel zoom
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoomLevel(prev => Math.max(0.1, Math.min(5, prev * delta)));
    }, []);

    // Handle mouse down to start panning
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
        setHasDragged(false);
    }, [panPosition]);

    // Handle mouse move to pan
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging) return;

        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // If moved more than 5 pixels, consider it a drag
        const distance = Math.sqrt(Math.pow(newX - panPosition.x, 2) + Math.pow(newY - panPosition.y, 2));
        if (distance > 5) {
            setHasDragged(true);
        }

        setPanPosition({ x: newX, y: newY });
    }, [isDragging, dragStart, panPosition]);

    // Handle mouse up to stop panning
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Handle preview click to open in FancyBox (only if not dragging).
    // Opens the whole sibling set as a gallery so the lightbox arrows work.
    const handlePreviewClick = useCallback(async (imageUrl: string) => {
        // Don't open FancyBox if the user was dragging
        if (hasDragged) {
            return;
        }

        if (!selectedImage || !currentBuilding) return;

        const entries = selectedPhotos;
        if (entries.length === 0) return;

        // Resolve every sibling's real download URL before opening so FancyBox
        // never caches a placeholder (which would otherwise require reopening).
        const resolvedUrls = await Promise.all(
            entries.map(photo =>
                resolveFirebaseImageUrl(photo.storagePath)
                    .catch(() => (photo.key === selectedImage.key ? imageUrl : photo.storagePath))
            )
        );
        const gallery = entries.map((photo, index) => ({
            src: resolvedUrls[index],
            caption: buildPhotoCaption(photo, users),
        }));
        const startIndex = entries.findIndex(p => p.key === selectedImage.key);

        Fancybox.show(gallery, {
            animated: true,
            showClass: 'fancybox-fadeIn',
            startIndex: startIndex >= 0 ? startIndex : 0,
        });
    }, [selectedImage, selectedPhotos, users, hasDragged, currentBuilding]);

    if (loadingBuilding) return <PageLoader />;
    if (buildingError) return <p style={{ color: "#ff0000" }}>{buildingError}</p>;
    if (!currentBuilding) return <p>No building data available</p>;
    if (!component) {
        const errorCrumbs: BreadcrumbItem[] = [];
        errorCrumbs.push({
            text: currentBuilding.summary.name ?? "Byggnad",
            link: `/building/${buildingKey}`
        });

        return (
            <div style={{
                backgroundColor: "#f4f4f4",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}>
                <div style={{ flexShrink: 0 }}>
                    <BreadcrumbComponent items={errorCrumbs} />
                </div>

                <div style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                    <div className="std-box" style={{
                        //backgroundColor: "#fff",
                        padding: "40px 60px",
                        //borderRadius: "8px",
                        /*boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",*/
                        textAlign: "center",
                        maxWidth: "500px"
                    }}>
                        <h2 style={{
                            color: "#333",
                            marginBottom: "16px",
                            fontSize: "24px"
                        }}>
                            Komponenten hittades inte
                        </h2>
                        <p style={{
                            color: "#666",
                            fontSize: "16px",
                            lineHeight: "1.5"
                        }}>
                            Denna komponent existerar inte eller har tagits bort.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Check if component has photos
    const hasPhotos = component.photos && Object.keys(component.photos).length > 0;

    // Check if component has documents
    const hasDocuments = component.documents && Object.keys(component.documents).length > 0;

    // Check if component has child components
    const hasChildComponents = (() => {
        if (!currentBuilding?.model?.structure?.components) return false;

        const components = currentBuilding.model.structure.components;
        return Object.values(components).some((comp: any) => {
            const parentSurfaces = [
                comp.parentSurface?.value,
                comp.parentSurface1?.value,
                comp.parentSurface2?.value
            ].filter(Boolean);
            return parentSurfaces.includes(component.key);
        });
    })();

    // Check if component has photo location
    const hasPhotoLocation = hasAnyPhotoLocation(component);

    // Only show placeholder if there's something to display
    const hasDisplayableContent = hasPhotos || hasChildComponents || hasDocuments || hasPhotoLocation;

    const crumbs: BreadcrumbItem[] = [];
    crumbs.push({
        text: currentBuilding.summary.name ?? "Byggnad",
        link: `/building/${buildingKey}`
    })

    let room: RoomImpl | undefined;

    room = resolveRoomFromComponentOrParents(component, currentBuilding);

    if (room) {
        if (room.levelKey) {
            const levelData = currentBuilding.model.structure?.levels?.[room.levelKey];
            if (levelData) {
                const level = new LevelImpl(levelData);
                crumbs.push({
                    text: level.displayName(),
                    link: `/building/${buildingKey}/level/${level.key}`,
                });
            }
        }

        crumbs.push({
            text: `${room.displayName()}`,
            link: `/building/${buildingKey}/room/${room.key}`,
        });
    }

    const baseTypes = NAVIGABLE_BASE_TYPES;

    // Check if component has a parent component (first parent surface)
    const parentSurfaceKeys = [
        (component as any).parentSurface?.value,
        (component as any).parentSurface1?.value,
        (component as any).parentSurface2?.value,
    ];
    const firstParentSurfaceKey = parentSurfaceKeys.find(key => key !== undefined);

    let parentImpl: BaseComponentImpl | null = null;
    let parentHasRoom = false;

    if (firstParentSurfaceKey && currentBuilding?.model?.structure?.components) {
        const parentComponent = currentBuilding.model.structure.components[firstParentSurfaceKey];
        if (parentComponent) {
            parentImpl = new BaseComponentImpl(parentComponent);
            // Check if parent has a room reference
            parentHasRoom = !!parentComponent.roomKey ||
                (!!parentComponent.parentRoomKeys && Array.isArray(parentComponent.parentRoomKeys.value) && parentComponent.parentRoomKeys.value.length > 0);
        }
    }

    // If the component has a parent without a room reference (like facade or roof), 
    // show the parent's path in the breadcrumb
    if (parentImpl && !parentHasRoom) {
        // Add parent type link (e.g., "Tak" or "Fasad")
        if (baseTypes.includes(parentImpl.type)) {
            crumbs.push({
                text: translate(parentImpl.type),
                link: `/building/${buildingKey}/${parentImpl.type}`
            });
        }

        // Add parent component link (e.g., "Tak Norr" or "Fasad Söder")
        crumbs.push({
            text: parentImpl.displayName(currentBuilding),
            link: `/building/${buildingKey}/component/${parentImpl.key}`
        });
    } else if (component.type === BaseType.Outdoor) {
        // Outdoor components use category-based navigation
        const outdoorCat = component.outdoorCategory as OutdoorCategory | undefined;
        const isValidCat = outdoorCat && Object.values(OutdoorCategory).includes(outdoorCat);
        crumbs.push({
            text: isValidCat ? translate(outdoorCat) : "Okategoriserat",
            link: `/building/${buildingKey}/outdoor/${isValidCat ? outdoorCat : 'uncategorized'}`
        });
    } else if (component.type === BaseType.Utility) {
        // Utility components use category-based navigation
        const utilityCat = component.utilityCategory as UtilityCategory | undefined;
        const isValidCat = utilityCat && Object.values(UtilityCategory).includes(utilityCat);
        crumbs.push({
            text: isValidCat ? translate(utilityCat) : "Okategoriserat",
            link: `/building/${buildingKey}/utilities/${isValidCat ? utilityCat : 'uncategorized'}`
        });
    } else if (component.type === BaseType.RoofSafety) {
        // RoofSafety components link to the roof safety list page
        crumbs.push({
            text: translate(BaseType.RoofSafety),
            link: `/building/${buildingKey}/roof-safety`
        });
    } else if (baseTypes.includes(component.type)) {
        // If no parent path is shown, add the current component type if it's navigable
        crumbs.push({
            text: translate(component.type),
            link: `/building/${buildingKey}/${component.type}`
        });
    }

    // Where the page goes once its component is deleted: the crumb above it
    const parentLink = crumbs[crumbs.length - 1].link ?? `/building/${buildingKey}`;

    crumbs.push({
        text: component.displayName(currentBuilding)
    })

    const handleComponentDeleted = (componentKey: string) => {
        setDeleteTarget(null);
        showToast('Komponenten togs bort');
        if (componentKey === component.key) {
            navigate(parentLink, { replace: true });
        } else {
            setSelectedChildKey(null);
        }
        // The delete also changes what it detached, and a parent component's page
        // reuses this one without fetching again, so re-read the building
        if (buildingKey) fetchBuilding(buildingKey).then();
    };

    return (
        <div style={{
            backgroundColor: "#f4f4f4",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            <div style={{ flexShrink: 0, marginBottom: '20px' }}>
                <BreadcrumbComponent
                    items={crumbs}
                />
            </div>

            <div style={{
                flex: 1,
                display: 'flex',
                overflow: 'hidden'
            }}>
                {/* Left Panel - Component Details (700px width) */}
                <div style={{
                    width: '700px',
                    backgroundColor: '#f4f4f4',
                    borderRight: '1px solid #dee2e6',
                    overflow: 'auto',
                    padding: '20px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0 }}>{component.displayName(currentBuilding)}</h3>
                        <ComponentActionsDropdown
                            buildingKey={buildingKey || ''}
                            componentKey={itemId || ''}
                            onReportIssue={() => setShowReportIssueModal(true)}
                            showOpenInNewTab={false}
                            size="large"
                            onDelete={() => setDeleteTarget(component)}
                        />
                    </div>

                    <div className="std-box"
                        style={{
                            width: "100%",
                            //borderRadius: "5px",
                            padding: "30px",
                            //backgroundColor: "#fff",
                            textAlign: "left",
                            /*boxShadow: '0 8px 8px rgba(0, 0, 0, 0.03)',*/
                        }}
                    >
                        {currentBuilding && buildingKey && (
                            <ComponentDetailsView
                                component={component}
                                building={currentBuilding}
                                buildingKey={buildingKey}
                                showHeader={false}
                                containerClassName=""
                                containerStyle={{
                                    backgroundColor: 'transparent',
                                    borderRadius: '0',
                                    padding: '0',
                                }}
                                onImageClick={hasPhotos ? (photo, _index, allPhotos) => handleImageSelect(photo, allPhotos) : undefined}
                                onChildComponentClick={handleChildComponentSelect}
                                selectedChildKey={selectedChildComponent?.key}
                                onDocumentClick={handleDocumentSelect}
                                onPhotoLocationClick={handlePhotoLocationSelect}
                                onChildPhotoClick={handleImageSelect}
                                selectedPhotoKey={selectedImage?.key}
                            />
                        )}
                    </div>
                </div>

                {/* Right Panel - Preview (flex: 1) */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#fff',
                    overflow: 'hidden'
                }}>
                    {selectedChildComponent ? (
                        // Show child component details
                        <div style={{
                            flex: 1,
                            overflow: 'auto',
                            padding: '20px',
                            backgroundColor: '#f4f4f4'
                        }}>
                            <div style={{ maxWidth: '800px', width: '100%' }}>
                                <ComponentDetailsView
                                    component={selectedChildComponent}
                                    building={currentBuilding}
                                    buildingKey={buildingKey || ''}
                                    showHeader={true}
                                    headerActions={
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <Link
                                                className="open-tab-button"
                                                to={`/building/${buildingKey}/component/${selectedChildComponent.key}`}
                                                target="_blank"
                                                title="Öppna i ny flik"
                                            >
                                                ↗
                                            </Link>
                                            <ComponentActionsDropdown
                                                buildingKey={buildingKey || ''}
                                                componentKey={selectedChildComponent.key}
                                                onReportIssue={() => setShowChildReportIssueModal(true)}
                                                showOpenInNewTab={false}
                                                resetTrigger={selectedChildComponent.key}
                                                onDelete={() => setDeleteTarget(selectedChildComponent)}
                                            />
                                        </div>
                                    }
                                />
                            </div>

                            {/* Child Component Report Issue Modal */}
                            {buildingKey && selectedChildComponent && (
                                <ReportIssueModal
                                    show={showChildReportIssueModal}
                                    onHide={() => setShowChildReportIssueModal(false)}
                                    buildingKey={buildingKey}
                                    componentKey={selectedChildComponent.key}
                                    onSuccess={() => {
                                        showToast('Felanmälan skickad');
                                    }}
                                />
                            )}
                        </div>
                    ) : selectedImage ? (
                        // Show image preview
                        <div
                            ref={previewRef}
                            style={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                cursor: isDragging ? 'grabbing' : 'grab'
                            }}
                            onWheel={handleWheel}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <ImagePreview
                                photo={selectedImage}
                                zoomLevel={zoomLevel}
                                panPosition={panPosition}
                                onWheel={handleWheel}
                                onClick={handlePreviewClick}
                                onMouseDown={handleMouseDown}
                                alt={component.displayName(currentBuilding)}
                                isDragging={isDragging}
                            />

                            {/* Prev/next arrows to step between sibling photos */}
                            {selectedPhotos.length > 1 && (() => {
                                const entries = selectedPhotos;
                                const currentIndex = entries.findIndex(p => p.key === selectedImage.key);
                                const arrowStyle: React.CSSProperties = {
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: 'rgba(0, 0, 0, 0.5)',
                                    color: '#fff',
                                    fontSize: '22px',
                                    lineHeight: '44px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    zIndex: 5,
                                };
                                return (
                                    <>
                                        <button
                                            aria-label="Föregående bild"
                                            title="Föregående bild (vänsterpil)"
                                            style={{ ...arrowStyle, left: '16px' }}
                                            onClick={(e) => { e.stopPropagation(); goToAdjacentPhoto(-1); }}
                                        >
                                            ‹
                                        </button>
                                        <button
                                            aria-label="Nästa bild"
                                            title="Nästa bild (högerpil)"
                                            style={{ ...arrowStyle, right: '16px' }}
                                            onClick={(e) => { e.stopPropagation(); goToAdjacentPhoto(1); }}
                                        >
                                            ›
                                        </button>
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '16px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: 'rgba(0, 0, 0, 0.6)',
                                            color: '#fff',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '13px',
                                            zIndex: 5,
                                        }}>
                                            {currentIndex + 1} / {entries.length}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    ) : selectedDocument ? (
                        // Show PDF preview
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: '#f5f5f5'
                        }}>
                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: '#fff',
                                borderBottom: '1px solid #dee2e6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <span style={{ fontWeight: 500 }}>{selectedDocument.name}.pdf</span>
                                <button
                                    onClick={() => window.open(selectedDocument.url, '_blank')}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        border: '1px solid #dee2e6',
                                        background: 'white',
                                        cursor: 'pointer',
                                        fontSize: '13px'
                                    }}
                                >
                                    Öppna i ny flik
                                </button>
                            </div>
                            <iframe
                                src={selectedDocument.url}
                                style={{
                                    flex: 1,
                                    width: '100%',
                                    border: 'none'
                                }}
                                title={selectedDocument.name}
                            />
                        </div>
                    ) : selectedPhotoLocation ? (
                        // Show PhotoWithMarker preview
                        <div style={{
                            flex: 1,
                            overflow: 'auto',
                            padding: '20px',
                            backgroundColor: '#f4f4f4'
                        }}>
                            <PhotoWithMarker
                                photoLocation={selectedPhotoLocation}
                                title="Position i foto"
                            />
                        </div>
                    ) : hasDisplayableContent ? (
                        // Show placeholder message only if there's content to display
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#666',
                            fontSize: '18px',
                            padding: '20px',
                            textAlign: 'center'
                        }}>
                            {hasPhotos || hasDocuments || hasPhotoLocation
                                ? 'Välj en bild, dokument eller underkomponent för förhandsvisning'
                                : 'Klicka på en underkomponent för att se detaljer'
                            }
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Report Issue Modal */}
            {buildingKey && (
                <ReportIssueModal
                    show={showReportIssueModal}
                    onHide={() => setShowReportIssueModal(false)}
                    buildingKey={buildingKey}
                    componentKey={component.key}
                    onSuccess={() => {
                        showToast('Felanmälan skickad');
                    }}
                />
            )}

            {deleteTarget && buildingKey && (
                <DeleteComponentModal
                    component={deleteTarget}
                    building={currentBuilding}
                    buildingKey={buildingKey}
                    onDeleted={handleComponentDeleted}
                    onClose={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
};

export default ComponentPage;
